import { ArrowLeftIcon, IdentificationIcon, ListBulletIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { RolodexModal } from 'pages/Loan/Parties/Modal/RolodexModal'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getBankerData } from 'services/apis/admin/admin'
import { svgLoading } from 'stories/assets'
import { Button, Input2, Modal, Pagination } from 'stories/components'
import { formatDate, getPrice3decimal, multiSelectRender, renderHeader } from 'utils'

interface AppraiserData {
  id?: number
  companyName?: string
  fullName?: string
  phone?: string
  email?: string
  address?: string
  licenseNumber?: string
  expireDate?: string
  [key: string]: any
}

interface Props {
  onClose: () => void
  onSelect: (appraiser: AppraiserData) => void
}

type ImportSource = 'choose' | 'eligible' | 'rolodex'

const mapRolodexToAppraiser = (item: Record<string, any>): AppraiserData => {
  const fullName = [item.FirstName, item.MiddleName, item.LastName].filter(Boolean).join(' ').trim()
  const address = [item.Street, item.City, [item.State, item.Zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  return {
    companyName: item.Company || '',
    fullName: fullName || String(item.Name || '').trim(),
    phone: item.WorkPhone || item.MobilePhone || item.HomePhone || '',
    email: item.EMail || item.EMail2 || item.EMail3 || '',
    address,
    licenseNumber: item.LicenseNo || '',
  }
}

export const ImportAppraiserModal = ({ onClose, onSelect }: Props) => {
  const [source, setSource] = useState<ImportSource>('choose')
  const [tableData, setTableData] = useState<AppraiserData[]>([])
  const [headers, setHeaders] = useState<any[]>([])
  const [inputs, setInputs] = useState<any>({})
  const [isLoading, setIsLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [query, setQuery] = useState<string | undefined>(undefined)
  const [filterValue, setFilterValue] = useState({
    query: '',
    pageNum: 0,
    orderBy: 'createdAt',
    orderDir: '-1',
  })

  useEffect(() => {
    if (source !== 'eligible') return
    fetchAppraisers()
  }, [source])

  useEffect(() => {
    if (query === undefined || source !== 'eligible') return
    const timeOutId = setTimeout(() => {
      if (!isLoading) {
        onChangeFilter('query', query)
      }
    }, 700)
    return () => clearTimeout(timeOutId)
  }, [query])

  const fetchAppraisers = async (filters: any = null) => {
    setIsLoading(true)
    try {
      const currentFilters = filters || filterValue
      const res = await getBankerData({
        table: 'appraiser',
        ...currentFilters,
        count: 10,
      })
      if (res.success) {
        setTableData(res.data || [])
        setHeaders(res.headers || [])
        setInputs(res.inputs || {})
        setTotalCount(res.total || 0)
      }
    } catch (error) {
      console.error('Failed to fetch appraisers:', error)
      toast('Failed to load appraisers', { type: 'error' })
    }
    setIsLoading(false)
  }

  const onChangeFilter = (key: string, value: any) => {
    const temp = cloneDeep(filterValue)
    ;(temp as any)[key] = value
    if (key === 'query') temp.pageNum = 0
    setFilterValue(temp)
    fetchAppraisers(temp)
  }

  const onSort = (key: string, dir: number) => {
    const temp = cloneDeep(filterValue)
    temp.orderBy = key
    temp.orderDir = `${dir}`
    setFilterValue(temp)
    fetchAppraisers(temp)
  }

  const handleRowClick = (appraiser: AppraiserData) => {
    onSelect(appraiser)
    onClose()
  }

  const onSelectRolodex = (item: Record<string, any> | null) => {
    if (item) onSelect(mapRolodexToAppraiser(item))
    onClose()
  }

  if (source === 'rolodex') {
    return <RolodexModal type="Appraiser" onClose={onSelectRolodex} />
  }

  if (source === 'choose') {
    return (
      <Modal title="Import Appraiser" isOpen onClose={onClose} titleOkay="" titleCancel="">
        <div className="w-[420px] max-w-full py-2">
          <p className="text-sm text-gray-600 mb-4">Choose where to import appraiser information from:</p>
          <div className="flex flex-col gap-3">
            <Button
              full
              onClick={() => {
                setIsLoading(true)
                setSource('eligible')
              }}
            >
              <span className="flex items-center justify-center gap-2">
                <ListBulletIcon className="w-5 h-5" />
                Eligible Appraiser List
              </span>
            </Button>
            <Button full color="gray" onClick={() => setSource('rolodex')}>
              <span className="flex items-center justify-center gap-2">
                <IdentificationIcon className="w-5 h-5" />
                Rolodex
              </span>
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Import Appraiser" isOpen onClose={onClose} titleOkay="" titleCancel="">
      <div className="w-[900px] max-w-full">
        <div className="relative">
          <LayoutLoading show={isLoading} />

          <div className="font-semibold text-[18px] flex justify-between items-center mb-4">
            <div className="flex gap-2 items-center">
              <span>Eligible Appraiser List</span>
              {isLoading && (
                <span className="ml-3">
                  <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
                </span>
              )}
            </div>
            <Button link onClick={() => setSource('choose')}>
              <span className="flex items-center gap-1 text-sm">
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </span>
            </Button>
          </div>

          <div className="flex items-center flex-wrap justify-between mb-4">
            <div className="filters flex flex-wrap items-center gap-3">
              <div className="min-w-[275px]">
                <Input2
                  type="search"
                  title="Search"
                  hasIcon
                  icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
                  value={query}
                  onChange={(value) => setQuery(value)}
                />
              </div>
            </div>
          </div>

          <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
              <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-3 py-3">
                    No
                  </th>
                  {headers.map(({ title, key }, index) =>
                    renderHeader({
                      title,
                      sortable: true,
                      key,
                      sortOrder: filterValue.orderBy == key ? parseInt(filterValue.orderDir) : 0,
                      index,
                      onSort: (key: string, dir: number) => onSort(key, dir),
                    }),
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={headers.length + 2} className="text-center py-8 text-gray-500">
                      No appraisers found
                    </td>
                  </tr>
                ) : (
                  tableData.map((row: any, index: number) => {
                    return (
                      <tr
                        className={`border-b ${
                          index % 2 ? 'bg-slate-50' : 'bg-white'
                        } hover:bg-blue-50 cursor-pointer transition-colors`}
                        key={`${index}`}
                        onClick={() => handleRowClick(row)}
                      >
                        <td
                          scope="row"
                          className="px-2 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap"
                        >
                          {filterValue.pageNum * 10 + index + 1}
                        </td>
                        {headers.map(({ key, value }, hIndex) => {
                          let rlt = value ? value(row) : row[key]
                          if (inputs[key]?.type === 'thousandSep' && rlt !== undefined) rlt = getPrice3decimal(rlt)
                          if (inputs[key]?.type === 'date' && rlt !== undefined) rlt = formatDate(rlt)
                          if (inputs[key]?.inputType === 'multiselect' && rlt !== undefined)
                            rlt = multiSelectRender(rlt)
                          if (typeof rlt == 'boolean') rlt = rlt ? 'Yes' : 'No'

                          return (
                            <td className="px-2 py-2" key={hIndex}>
                              {typeof rlt === 'object' ? rlt : <span dangerouslySetInnerHTML={{ __html: rlt }} />}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
            <div className="flex justify-end items-center mt-3 mb-3">
              <Pagination
                totalCount={totalCount}
                itemCountPerPage={10}
                onNavigate={(num) => onChangeFilter('pageNum', num)}
                pageNum={filterValue.pageNum}
              />
            </div>
          </div>

          <div className="text-xs text-gray-400 mt-3 text-center">Click on a row to import the appraiser data</div>
        </div>
      </div>
    </Modal>
  )
}
